/**
 * Continuous animal bodies.
 *
 * Kate asked twice for real animals rather than cartoons, and Howard confirmed after
 * playing that they still look cartoonish. The cause is structural, not textural: the
 * bodies were stacks of separate capsules, spheres and cones, so you can see the seams
 * where a head ball meets a neck tube meets a body capsule. No amount of fur texture
 * fixes a creature made of visibly glued parts.
 *
 * This builds a body as ONE surface instead — an ellipse swept along a spine curve, with
 * the cross-section changing shape and size from nose to tail. That is how a real animal
 * is shaped: a continuous form that swells at the ribcage, narrows at the waist, and
 * tapers into the tail.
 *
 * Ambient occlusion is baked into vertex colours so the underside and the crevices sit in
 * shadow, which is a large part of what makes a real photograph read as solid.
 */
import * as THREE from 'three'

export interface SpineNode {
  /** Distance along the body, 0 at the nose, 1 at the tail tip. */
  t: number
  /** Centre of the cross-section, relative to the body's origin. */
  y: number
  z: number
  /** Half-width and half-height of the elliptical cross-section here. */
  width: number
  height: number
}

/** Catmull-Rom through the control points, so the surface is smooth, not faceted. */
function sampleSpine(nodes: SpineNode[], t: number): SpineNode {
  if (t <= nodes[0]!.t) return nodes[0]!
  if (t >= nodes[nodes.length - 1]!.t) return nodes[nodes.length - 1]!

  let i = 0
  while (i < nodes.length - 2 && nodes[i + 1]!.t < t) i++
  const p0 = nodes[Math.max(0, i - 1)]!
  const p1 = nodes[i]!
  const p2 = nodes[i + 1]!
  const p3 = nodes[Math.min(nodes.length - 1, i + 2)]!
  const span = p2.t - p1.t
  const local = span > 0 ? (t - p1.t) / span : 0

  const spline = (a: number, b: number, c: number, d: number, x: number): number => {
    const x2 = x * x
    const x3 = x2 * x
    return 0.5 * ((2 * b) + (-a + c) * x + (2 * a - 5 * b + 4 * c - d) * x2 + (-a + 3 * b - 3 * c + d) * x3)
  }

  return {
    t,
    y: spline(p0.y, p1.y, p2.y, p3.y, local),
    z: spline(p0.z, p1.z, p2.z, p3.z, local),
    width: Math.max(0.001, spline(p0.width, p1.width, p2.width, p3.width, local)),
    height: Math.max(0.001, spline(p0.height, p1.height, p2.height, p3.height, local)),
  }
}

export interface LoftOptions {
  /** Cross-sections along the body. More means smoother. */
  segments?: number
  /** Points around each cross-section. */
  radial?: number
  /**
   * Squares off the belly and rounds the back, instead of a plain ellipse — real
   * animals are not circular in section.
   */
  bellyFlatten?: number
  /** How strongly to darken the underside and the crevices. */
  occlusion?: number
}

/**
 * Sweep an ellipse along the spine to make one continuous body.
 * Returns a geometry with smooth normals and baked occlusion in vertex colours.
 */
export function loftBody(nodes: SpineNode[], options: LoftOptions = {}): THREE.BufferGeometry {
  const segments = options.segments ?? 56
  const radial = options.radial ?? 24
  const bellyFlatten = options.bellyFlatten ?? 0.22
  const occlusion = options.occlusion ?? 0.45

  const positions: number[] = []
  const normals: number[] = []
  const uvs: number[] = []
  const colors: number[] = []
  const indices: number[] = []

  for (let s = 0; s <= segments; s++) {
    const t = s / segments
    const node = sampleSpine(nodes, t)
    // Taper the very ends to a point so the nose and tail close cleanly.
    const endTaper = Math.sin(Math.min(1, Math.min(t, 1 - t) * 14) * Math.PI * 0.5)
    const w = node.width * endTaper
    const h = node.height * endTaper

    for (let r = 0; r <= radial; r++) {
      const a = (r / radial) * Math.PI * 2
      const cos = Math.cos(a)
      const sin = Math.sin(a)

      // Flatten the belly: pull the lower half of the section inward.
      const belly = sin < 0 ? 1 - bellyFlatten * -sin : 1
      const x = cos * w
      const y = node.y + sin * h * belly
      const z = node.z

      positions.push(x, y, z)
      // Normal points out from the section's centre.
      const nx = cos / Math.max(0.0001, w)
      const ny = sin / Math.max(0.0001, h)
      const len = Math.hypot(nx, ny) || 1
      normals.push(nx / len, ny / len, 0)
      uvs.push(r / radial, t)

      // Bake occlusion: undersides and the narrow parts of the body sit darker, the way
      // light actually falls on a solid animal.
      const facingDown = Math.max(0, -sin)
      const tightness = 1 - Math.min(1, node.height / 0.6)
      const shade = 1 - occlusion * (facingDown * 0.75 + tightness * 0.25)
      colors.push(shade, shade, shade)
    }
  }

  const ring = radial + 1
  for (let s = 0; s < segments; s++) {
    for (let r = 0; r < radial; r++) {
      const a = s * ring + r
      const b = a + ring
      indices.push(a, b, a + 1, b, b + 1, a + 1)
    }
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3))
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))
  geometry.setIndex(indices)
  // Recompute so the swept surface shades as one smooth form rather than by section.
  geometry.computeVertexNormals()
  return geometry
}

/** A tapered limb, also one continuous surface rather than a stack. */
export function loftLimb(
  topRadius: number, midRadius: number, footRadius: number, length: number,
  radial = 14,
): THREE.BufferGeometry {
  const nodes: SpineNode[] = [
    { t: 0, y: 0, z: 0, width: topRadius, height: topRadius },
    { t: 0.32, y: -length * 0.32, z: 0, width: midRadius, height: midRadius },
    { t: 0.72, y: -length * 0.72, z: 0, width: midRadius * 0.82, height: midRadius * 0.82 },
    { t: 1, y: -length, z: 0, width: footRadius, height: footRadius },
  ]
  // Sweep down Y rather than Z for a limb.
  const positions: number[] = []
  const normals: number[] = []
  const uvs: number[] = []
  const colors: number[] = []
  const indices: number[] = []
  const segments = 18

  for (let s = 0; s <= segments; s++) {
    const t = s / segments
    const node = sampleSpine(nodes, t)
    for (let r = 0; r <= radial; r++) {
      const a = (r / radial) * Math.PI * 2
      positions.push(Math.cos(a) * node.width, node.y, Math.sin(a) * node.width)
      normals.push(Math.cos(a), 0, Math.sin(a))
      uvs.push(r / radial, t)
      // Legs darken toward the top, where they meet the body.
      const shade = 0.72 + 0.28 * t
      colors.push(shade, shade, shade)
    }
  }
  const ring = radial + 1
  for (let s = 0; s < segments; s++) {
    for (let r = 0; r < radial; r++) {
      const a = s * ring + r
      const b = a + ring
      indices.push(a, b, a + 1, b, b + 1, a + 1)
    }
  }

  // Cap both ends. Without this a limb is an open pipe, and you see straight up the
  // inside of a thigh or a paw whenever the end faces the camera.
  for (const end of [0, 1] as const) {
    const node = sampleSpine(nodes, end)
    const centre = positions.length / 3
    positions.push(0, node.y, 0)
    normals.push(0, end === 0 ? 1 : -1, 0)
    uvs.push(0.5, end)
    const shade = 0.72 + 0.28 * end
    colors.push(shade, shade, shade)
    const first = end === 0 ? 0 : segments * ring
    for (let r = 0; r < radial; r++) {
      // Wind each fan so its face points away from the limb, not into it.
      if (end === 0) indices.push(centre, first + r, first + r + 1)
      else indices.push(centre, first + r + 1, first + r)
    }
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3))
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))
  geometry.setIndex(indices)
  geometry.computeVertexNormals()
  return geometry
}
